import axios from 'axios';

let menuRequestSequence = 0;

/**
 * MenuService - Centralized menu loading for course navigation
 *
 * This service provides a consistent way to load menu data across all components,
 * replacing the old pattern of adding ?menu=true to various endpoints.
 */
class MenuService {
    /**
     * Load menu data for a specific course
     * @param {number} courseId - The course ID
     * @param {Object} [config] - Axios request config, such as an abort `signal`
     * @returns {Promise<Object>} - Menu data from the dedicated menu endpoint
     */
    async loadCourseMenu(courseId, config = {}) {
        try {
            const response = await axios.get(`/api/courses/${courseId}/menu?append=completed`, config);
            return response.data;
        } catch (error) {
            console.error('Failed to load course menu:', error);
            throw error;
        }
    }

    /**
     * Load the menu the store will show. This request becomes the one requested last, and
     * `setCourseMenu` drops a later-arriving menu from any earlier request, even one for the
     * same course. If the request fails, the store stops waiting for it.
     * @param {Object} store - Vuex store instance
     * @param {number} courseId - The course ID
     * @param {Object} [config] - Axios request config, such as an abort `signal`
     * @returns {{ request: Promise<Object>, requestId: number }} - the menu request and the id to pass to `setCourseMenu`
     */
    requestCourseMenu(store, courseId, config = {}) {
        menuRequestSequence += 1;
        const requestId = menuRequestSequence;

        store.dispatch('Menu/requestMenuForCourse', { courseId, requestId }, { root: true });
        const request = this.loadCourseMenu(courseId, config);
        request.catch(() => store.dispatch('Menu/dropMenuRequest', { courseId, requestId }, { root: true }));

        return { request, requestId };
    }

    /**
     * Load menu data and dispatch to Vuex store
     * @param {Object} store - Vuex store instance
     * @param {number} courseId - The course ID
     * @returns {Promise<Object>} - Menu data
     */
    async loadAndSetCourseMenu(store, courseId) {
        const { request, requestId } = this.requestCourseMenu(store, courseId);
        const menuData = await request;
        await this.setCourseMenu(store, courseId, menuData, requestId);
        return menuData;
    }

    /**
     * Dispatch already-loaded menu data to the Vuex store
     * @param {Object} store - Vuex store instance
     * @param {number} courseId - The course ID
     * @param {Object} menuData - Menu data from the dedicated menu endpoint
     * @param {number|null} [requestId] - the id `requestCourseMenu` returned for this menu
     * @returns {Promise<void>}
     */
    async setCourseMenu(store, courseId, menuData, requestId = null) {
        await store.dispatch('Menu/setMenuForCourse', { data: menuData, courseId, requestId }, { root: true });
    }

    /**
     * Legacy method for backward compatibility
     * @deprecated Use loadAndSetCourseMenu instead
     */
    async loadCourseWithMenu(courseId) {
        console.warn('MenuService.loadCourseWithMenu is deprecated. Use loadAndSetCourseMenu instead.');
        return this.loadCourseMenu(courseId);
    }
}

export default new MenuService();
